import { loginRequest } from '@/config/authConfig';
import { env } from '@/config/env.config';
import { msalInstance } from '@/config/msalInstance';
import Logger from '@/lib/logger';
import { isDevtoolsEnvironment } from '@/utils/env.utils';
import {
  ensureMsalInitialized,
  refreshAccessToken,
} from '@/utils/tokenRefresh';

/**
 * Dev-tools experiment: send an MSAL access token for an API scope as the
 * bearer instead of the ID token. Microsoft advises against calling APIs with
 * ID tokens; this lets dev and QA check whether the backend accepts access
 * tokens before any real migration.
 *
 * Safety: the setting is read and written only in dev tools environments
 * (dev, QA). Everywhere else every function here behaves as "ID token", even
 * if a value is present in storage.
 */
export type ApiBearerMode = 'id-token' | 'access-token';

export interface ApiBearerSettings {
  mode: ApiBearerMode;
  scope: string;
}

// Holds only the selected mode and the public scope name, never a token.
export const API_BEARER_STORAGE_KEY = 'atlas_devtools_request_auth_mode';
export const DEFAULT_API_BEARER_SETTINGS: ApiBearerSettings = {
  mode: 'id-token',
  scope: '',
};

/** Request hooks that build headers themselves and so always send the ID token. */
export const API_BEARER_UNCOVERED_REQUESTS = [
  'onboarding mutations (temp member, OTP, register, agreements, health questionnaire, create member)',
  'completeRegistration and booking from a temp member',
  'support event form page',
] as const;

const isToggleAvailable = isDevtoolsEnvironment(env.VITE_ENVIRONMENT);
const listeners = new Set<() => void>();

function normalizeScope(scope: unknown): string {
  return typeof scope === 'string' ? scope.trim() : '';
}

export function isValidApiScope(scope: string): boolean {
  return scope.length > 0 && !/\s/.test(scope);
}

function readStoredSettings(): ApiBearerSettings {
  if (!isToggleAvailable) return DEFAULT_API_BEARER_SETTINGS;

  try {
    const raw = window.localStorage.getItem(API_BEARER_STORAGE_KEY);
    if (!raw) return DEFAULT_API_BEARER_SETTINGS;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return DEFAULT_API_BEARER_SETTINGS;
    }

    const record = parsed as Record<string, unknown>;
    const scope = normalizeScope(record.scope);
    const mode: ApiBearerMode =
      record.mode === 'access-token' && isValidApiScope(scope)
        ? 'access-token'
        : 'id-token';

    return { mode, scope };
  } catch {
    return DEFAULT_API_BEARER_SETTINGS;
  }
}

let currentSettings = readStoredSettings();

export function isApiBearerToggleAvailable(): boolean {
  return isToggleAvailable;
}

export function getApiBearerSettings(): ApiBearerSettings {
  return isToggleAvailable ? currentSettings : DEFAULT_API_BEARER_SETTINGS;
}

export function subscribeApiBearerSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setApiBearerSettings(next: ApiBearerSettings): void {
  if (!isToggleAvailable) return;

  const scope = normalizeScope(next.scope);
  currentSettings = {
    mode:
      next.mode === 'access-token' && isValidApiScope(scope)
        ? 'access-token'
        : 'id-token',
    scope,
  };

  try {
    window.localStorage.setItem(
      API_BEARER_STORAGE_KEY,
      JSON.stringify(currentSettings)
    );
  } catch {
    // The setting still applies to this page; it just will not survive reload.
  }

  listeners.forEach((listener) => listener());
}

export function isAccessTokenBearerActive(): boolean {
  const { mode, scope } = getApiBearerSettings();
  return mode === 'access-token' && isValidApiScope(scope);
}

/**
 * Scopes for sign-in. With the access-token bearer on, sign-in asks for the
 * API scope so consent and the first access token come with the login.
 */
export function getLoginRequest(): typeof loginRequest {
  if (!isAccessTokenBearerActive()) return loginRequest;
  return { ...loginRequest, scopes: [getApiBearerSettings().scope] };
}

function abortError(signal: AbortSignal): unknown {
  return (
    signal.reason ?? new DOMException('The operation was aborted', 'AbortError')
  );
}

function withSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError(signal));

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError(signal));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      }
    );
  });
}

/**
 * The bearer for an authenticated request. In the default mode this is the
 * session's ID token unchanged. With the access-token bearer on, it is an
 * access token for the configured scope; MSAL caches and renews it.
 *
 * Rejects with InteractionRequiredAuthError when consent or sign-in is needed.
 */
export async function resolveApiBearer(
  idToken: string | undefined,
  {
    forceRefresh = false,
    signal,
  }: { forceRefresh?: boolean; signal?: AbortSignal } = {}
): Promise<string | undefined> {
  if (!idToken || !isAccessTokenBearerActive()) return idToken;

  await withSignal(ensureMsalInitialized(), signal);
  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  // Not an MSAL session (for example a backend-issued temp auth token).
  if (!account) return idToken;

  const result = await withSignal(
    msalInstance.acquireTokenSilent({
      scopes: [getApiBearerSettings().scope],
      account,
      forceRefresh,
    }),
    signal
  );

  Logger.debug('apiBearer: using access token bearer');
  return result.accessToken;
}

/**
 * A replacement bearer after the API answered 401. Resolves null when no
 * renewal is possible right now; rejects with InteractionRequiredAuthError
 * when only an interactive sign-in can recover.
 */
export async function refreshApiBearer({
  currentIdToken,
  signal,
}: {
  currentIdToken: string;
  signal?: AbortSignal;
}): Promise<string | null> {
  if (isAccessTokenBearerActive()) {
    return (
      (await resolveApiBearer(currentIdToken, {
        forceRefresh: true,
        signal,
      })) ?? null
    );
  }

  const tokens = await refreshAccessToken({ signal, currentIdToken });
  return tokens?.idToken ?? null;
}
